import axios from 'axios';

// Use relative URL for same-domain deployments (like Vercel)
// Use full URL only for cross-domain deployments
const API_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add request interceptor for logging
api.interceptors.request.use(request => {
  return request;
});

// Add response interceptor for logging
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

export const generateCompanyInfo = async (companyName) => {
  try {
    const response = await api.post('/company/summary', { companyName });
    return response.data.data;
  } catch (error) {
    console.error('Error generating company info:', error);
    throw error;
  }
};

export const generateDocument = async (documentType, companyInfo, inputFields, feedback = null) => {
  try {
    const response = await api.post('/company/generate-document', {
      documentType,
      companyInfo,
      inputFields,
      feedback
    });
    
    return response.data.data;
  } catch (error) {
    console.error('Error generating document:', error);
    throw error;
  }
};

export const generateEnhancedDocument = async (documentType, companyInfo, inputFields, customPrompt = null) => {
  try {
    const response = await api.post('/compass/generate-enhanced-document', {
      documentType,
      companyInfo,
      inputFields,
      customPrompt
    });
    
    return response.data.data;
  } catch (error) {
    console.error('Error generating enhanced document:', error);
    throw error;
  }
};

export const generateCompanySummary = async (companyName) => {
  try {
    if (!companyName?.trim()) {
      throw new Error('Company name is required');
    }

    const response = await api.post('/company/summary', {
      companyName: companyName.trim()
    });

    if (response.data?.status !== 'success') {
      throw new Error(response.data?.error || 'Failed to generate company summary');
    }

    if (!response.data?.data?.summary) {
      throw new Error('Invalid response format: missing summary');
    }

    return response.data.data;
  } catch (error) {
    console.error('Error in generateCompanySummary:', {
      companyName,
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    
    // Enhance error message based on the type of error
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    } else if (error.response?.status === 404) {
      throw new Error('API endpoint not found. Please check server configuration.');
    } else if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    } else if (!error.response) {
      throw new Error('Network error. Please check your connection.');
    }
    
    throw error;
  }
};

export const saveCompanyToDatabase = async (companyData) => {
  try {
    const response = await api.post('/company', companyData);
    return response.data.data.company;
  } catch (error) {
    console.error('Error saving company to database:', error);
    throw error;
  }
};

export const checkCompanyExists = async (companyName) => {
  try {
    const response = await api.get(`/company/exists/${encodeURIComponent(companyName)}`);
    return response.data.data;
  } catch (error) {
    console.error('Error checking company existence:', error);
    throw error;
  }
};

export const searchCompanies = async (query) => {
  try {
    const response = await api.get(`/company/search?q=${encodeURIComponent(query)}`);
    return response.data.data.companies;
  } catch (error) {
    console.error('Error searching companies:', error);
    throw error;
  }
}; 